import { ref, watch } from 'vue';

/**
 * Manages tool consent state — tracking which tools the user has permanently approved.
 * Auto-approves interrupts when all requested tools are already consented.
 */
export function useCopilotConsents({ basePath, getAuthHeaders, stream }) {
  const toolConsents = ref([]);

  async function fetchToolConsents() {
    try {
      const res = await fetch(`${basePath}/settings`, {
        headers: getAuthHeaders(),
        credentials: 'same-origin',
      });
      if (res.ok) {
        const data = await res.json();
        toolConsents.value = data.tool_consents || [];
      }
    } catch {
      /* Non-critical */
    }
  }

  async function persistConsents(updated) {
    try {
      await fetch(`${basePath}/settings`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ tool_consents: updated }),
      });
    } catch {
      /* Non-critical */
    }
  }

  async function addToolConsent(toolName) {
    const updated = [...new Set([...toolConsents.value, toolName])];
    toolConsents.value = updated;
    await persistConsents(updated);
  }

  async function removeToolConsent(toolName) {
    const updated = toolConsents.value.filter(t => t !== toolName);
    toolConsents.value = updated;
    await persistConsents(updated);
  }

  // Auto-approve tool consent interrupts when all tools are already consented
  watch(
    () => stream.interrupt.value,
    newInterrupt => {
      if (!newInterrupt) return;
      const value = newInterrupt.value ?? newInterrupt;

      let pendingTools = [];
      let resumePayload = { approved: true };

      if (value?.type === 'tool_consent') {
        pendingTools = value.tools || [];
      } else if (Array.isArray(value?.action_requests)) {
        pendingTools = value.action_requests
          .map(r => r.name || r.tool)
          .filter(Boolean);
        resumePayload = {
          decisions: value.action_requests.map(() => ({ type: 'approve' })),
        };
      } else {
        return;
      }

      // [].every() is true — require at least one tool before auto-approving.
      const allConsented =
        pendingTools.length > 0 &&
        pendingTools.every(t => toolConsents.value.includes(t));
      if (allConsented) {
        stream.submit(undefined, { command: { resume: resumePayload } });
      }
    }
  );

  return { toolConsents, fetchToolConsents, addToolConsent, removeToolConsent };
}
